import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    LinearProgress,
    Alert,
    IconButton,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { fileService, analysisService } from '../../services';
import { formatFileSize, getFileExtension } from '../../utils/fileValidation';
import { UPLOAD_CONFIG } from '../../utils/constants';
import { UploadProgress, ValidationError } from '../../types';

// Get file icon based on extension
const getFileIcon = (filename: string) => {
    const ext = getFileExtension(filename);
    switch (ext) {
        case '.zip':
            return FolderZipIcon;
        default:
            return InsertDriveFileIcon;
    }
};

const UploadPage = () => {
    const navigate = useNavigate();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setUploadError(null);
        setValidationErrors([]);
        setUploadSuccess(false);

        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            const validation = fileService.validateFile(file);

            if (!validation.valid) {
                setValidationErrors(validation.errors);
                return;
            }

            setSelectedFile(file);
        }
    }, []);

    const onDropRejected = useCallback(() => {
        setUploadError('Invalid file. Please make sure the format is SHP, ZIP, or GPKG.');
    }, []);

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop,
        onDropRejected,
        accept: UPLOAD_CONFIG.acceptedFormats,
        maxSize: UPLOAD_CONFIG.maxFileSize,
        multiple: false,
    });

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setUploadProgress(null);
        setUploadError(null);
        setValidationErrors([]);
        setUploadSuccess(false);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setUploadError(null);

        try {
            const fileInfo = await fileService.uploadFile(selectedFile, (progress) => {
                setUploadProgress(progress);
            });

            setUploadSuccess(true);

            // Start analysis
            const analysisResult = await analysisService.startAnalysis({
                fileId: fileInfo.id,
                analysisType: 'full',
            });

            // Navigate to processing page with analysis ID
            setTimeout(() => {
                navigate('/processing', { state: { analysisId: analysisResult.id } });
            }, 500);
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : 'Upload failed');
            setIsUploading(false);
        }
    };

    const FileIcon = selectedFile ? getFileIcon(selectedFile.name) : InsertDriveFileIcon;

    return (
        <Box
            sx={{
                minHeight: '80vh',
                py: { xs: 4, md: 8 },
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
            }}
        >
            <Container maxWidth="md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
                            Upload Spatial File
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                            Choose a SHP, ZIP, or GPKG file to start analysis. Maximum file size 50MB.
                        </Typography>
                    </Box>

                    {/* Drop Zone */}
                    <Paper
                        {...getRootProps()}
                        elevation={0}
                        sx={{
                            p: 6,
                            borderRadius: 4,
                            border: '2px dashed',
                            borderColor: isDragReject
                                ? 'error.main'
                                : isDragActive
                                    ? 'primary.main'
                                    : 'divider',
                            backgroundColor: isDragActive
                                ? 'primary.50'
                                : isDragReject
                                    ? 'error.50'
                                    : 'background.paper',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'grey.50',
                            },
                        }}
                    >
                        <input {...getInputProps()} />

                        <AnimatePresence mode="wait">
                            {!selectedFile ? (
                                <motion.div
                                    key="dropzone"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0.2) 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 3,
                                        }}
                                    >
                                        <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        {isDragActive ? 'Drop the file here' : 'Drag & drop file here'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        or click to browse
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Format: SHP, ZIP, GPKG • Max: 50MB
                                    </Typography>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="file-preview"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 3,
                                            py: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: 2,
                                                backgroundColor: 'primary.50',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <FileIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                        </Box>
                                        <Box sx={{ textAlign: 'left', flex: 1, maxWidth: 300 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 600,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {selectedFile.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatFileSize(selectedFile.size)}
                                            </Typography>
                                            {uploadProgress && (
                                                <Box sx={{ mt: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={uploadProgress.percentage}
                                                        sx={{ height: 6, borderRadius: 3 }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {uploadProgress.percentage}%
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        {!isUploading && !uploadSuccess && (
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveFile();
                                                }}
                                                sx={{ color: 'error.main' }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                        {uploadSuccess && (
                                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />
                                        )}
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Paper>

                    {/* Validation Errors */}
                    <AnimatePresence>
                        {validationErrors.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                {validationErrors.map((error, index) => (
                                    <Alert
                                        key={index}
                                        severity="error"
                                        icon={<ErrorIcon />}
                                        sx={{ mt: 2, borderRadius: 2 }}
                                    >
                                        {error.message}
                                    </Alert>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Upload Error */}
                    <AnimatePresence>
                        {uploadError && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                                    {uploadError}
                                </Alert>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Upload Button */}
                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleUpload}
                                disabled={!selectedFile || isUploading || uploadSuccess}
                                sx={{
                                    px: 6,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.35)',
                                    '&:disabled': {
                                        background: 'grey.300',
                                    },
                                }}
                            >
                                {isUploading ? 'Uploading...' : uploadSuccess ? 'Success!' : 'Start Analysis'}
                            </Button>
                        </motion.div>
                    </Box>

                    {/* File Format Info */}
                    <Box sx={{ mt: 6 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                            Supported File Formats
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 4,
                                flexWrap: 'wrap',
                            }}
                        >
                            {[
                                { ext: '.ZIP', desc: 'Shapefile (archive)' },
                                { ext: '.SHP', desc: 'ESRI Shapefile' },
                                { ext: '.GPKG', desc: 'GeoPackage' },
                            ].map((format) => (
                                <Box key={format.ext} sx={{ textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            px: 3,
                                            py: 1,
                                            borderRadius: 2,
                                            backgroundColor: 'primary.50',
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            mb: 0.5,
                                        }}
                                    >
                                        {format.ext}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {format.desc}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};

export default UploadPage;
